<template>
    <a class="skip-link" href="#content" tabindex="1">
      Skip to content
    </a>
    <header-component v-if="$route.meta.showHeader"></header-component>
    <cookie-bar v-if="showPopup" @close-popup="onClosePopup"/>


        <router-view v-slot="{ Component }">
            <transition name="up" mode="out-in" @after-enter="onAfterEnter" @before-leave="onBeforeLeave">
                <Component :is="Component" :class=" isTransitioning? 'hide-content' : ''" class="show-content"/>
            </transition>
            
            <transition name="footer" @before-enter="onFooterBeforeEnter" @after-leave="onFooterAfterLeave">
                <footer-component v-if="$route.meta.showFooter" :class=" isTransitioning? 'hide-content' : ''"/>
            </transition>
        </router-view>

</template>

<script lang="ts">

    import { createApp, defineComponent, ref, onUpdated, onMounted, nextTick, watch } from 'vue'
    import router from './routes'
    import { useQuery } from '@vue/apollo-composable'
    import gql from 'graphql-tag'
    import { useRoute } from 'vue-router'

    import { useUserway } from './composables/useUserway'
    import { setupGlobalErrorHandler } from './composables/useErrorHandler'
    // import { performanceMonitor } from './utils/performance-monitor'

    import HeaderComponent from "./components/navigation/Header.vue";
    import FooterComponent from "./components/navigation/Footer.vue";
    import CookieBar from "./components/navigation/CookieBar.vue";
    import { gsap } from "gsap";
    import SplitText from "gsap/SplitText"
    import ScrollTrigger from "gsap/ScrollTrigger"
    import whatInput from 'what-input';

    gsap.registerPlugin(ScrollTrigger, SplitText)

    const hasConsentedToCookie = () => {
        return document.cookie.includes('cookieConsent=true') || document.cookie.includes('cookieConsent=false');
    };


    export default defineComponent({
        components: {
            HeaderComponent,
            FooterComponent,
            CookieBar
        },

        setup() {
            const showPopup = ref(!hasConsentedToCookie());
            const animatedHeadings = ref([]);
            const animatedHeadingsLoaded = ref(false);
            const route = useRoute();

            // Initialize UserWay composable
            // const { isLoaded: userwayLoaded, config: userwayConfig, error: userwayError, loadUserwayConfig } = useUserway();

            // Safari detection for performance optimization
            const isSafari = () => {
                return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
            };

            const onClosePopup = () => {
                showPopup.value = false;
            };

            const HeadingsAnimation = () => {

                const animatedHeadings = document.querySelectorAll('.animated-heading');

                animatedHeadings.forEach((heading) => {

                    let type = heading.getAttribute('data-gsap-type') ?? 'lines'

                    // Regular split text animation for non-Safari browsers
                    const splitText = new SplitText(heading, { type: 'words, lines, chars' });

                    // Store attribute values as strings first
                    const stagger = heading.getAttribute('data-gsap-stagger') ?? 0.07;
                    const delay = heading.getAttribute('data-gsap-delay') ?? 0;

                    let startVal = heading.getAttribute('data-gsap-start') ?? 'top 90%'
                    let endVal = heading.getAttribute('data-gsap-end') ?? 'bottom center'
                    let duration = heading.getAttribute('data-gsap-duration') ?? 0.8
                    let elements = splitText.words;
                    let distance = heading.getAttribute('data-gsap-distance') ?? 37 
                    let initialScale = heading.getAttribute('data-gsap-initial-scale') ?? 1

                    if(type === 'lines'){
                        elements = splitText.lines;
                    } else if(type === 'chars'){
                        elements = splitText.chars;
                    }

                    // Add hardware acceleration to all split elements
                    gsap.set(elements, { 
                        opacity: 0, 
                        scale: 0.7,
                        force3D: true,
                        backfaceVisibility: 'hidden',
                        perspective: 1000,
                        transformStyle: 'preserve-3d',
                        transform: 'translateZ(0)',
                        willChange: 'transform, opacity'
                    });

                    gsap.fromTo(
                        elements,
                    {
                        opacity: 0,
                        y: distance,
                        scale: initialScale,
                        force3D: true
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: duration,
                        stagger: stagger as any,
                        delay: delay,
                        ease: "power3.out",
                        force3D: true,
                        willChange: 'transform, opacity',

                        scrollTrigger: {
                            trigger: heading,
                            markers: false,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });
                });

            }

            const ImageAnimations = () => {
            

                const images = document.querySelectorAll('.image-parallax');

                images.forEach((image) => {

                    let startVal = image.getAttribute('data-gsap-start') ?? 'top 85%'
                    let endVal = image.getAttribute('data-gsap-end') ?? 'bottom center'
                    let delay = image.getAttribute('data-gsap-delay') ?? 0

                    // Add hardware acceleration
                    gsap.set(image, {
                        transform: 'translateZ(0)',
                        backfaceVisibility: 'hidden'
                    });

                    gsap.fromTo(
                        image,
                    {
                        opacity: 0,
                        y: 40,
                        scale: 0.8,
                        force3D: true,
                        transform: 'translateZ(0)'
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.4,
                        delay: delay,
                        ease: "easeInOut",
                        force3D: true,
                        willChange: 'transform, opacity',

                        scrollTrigger: {
                            trigger: image,
                            markers: false,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });
                });
            }

            const fadeIns = () => {

                let elements = document.querySelectorAll('[data-gsap="fadeIn"]');


                elements.forEach((element) => {

                    let startVal = element.getAttribute('data-gsap-start') ?? 'top 85%'
                    let endVal = element.getAttribute('data-gsap-end') ?? 'bottom center'
                    let delay = element.getAttribute('data-gsap-delay') ?? 0
                    let initialScale = element.getAttribute('data-gsap-initial-scale') ?? 0.5
                    let duration = element.getAttribute('data-gsap-duration') ?? 0.6
                    let ease = element.getAttribute('data-gsap-ease') ?? 'power2.out'

                    // Add hardware acceleration for better performance
                    gsap.set(element, {
                        transform: 'translateZ(0)',
                        backfaceVisibility: 'hidden',
                        willChange: 'opacity'
                    });

                    gsap.fromTo(
                        element,
                    {
                        opacity: 0,
                        force3D: true
                    },
                    {
                        opacity: 1,
                        duration: duration,
                        delay: delay,
                        ease: ease,
                        force3D: true,
                        willChange: 'opacity',

                        scrollTrigger: {
                            trigger: element,
                            markers: false,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });

                });
            }
            const fadeInRight = () => {
                
            let elements = document.querySelectorAll('[data-gsap="fadeInRight"]');

                elements.forEach((element) => {

                    let startVal = element.getAttribute('data-gsap-start') ?? 'top 85%'
                    let endVal = element.getAttribute('data-gsap-end') ?? 'bottom center'
                    let delay = element.getAttribute('data-gsap-delay') ?? 0
                    let duration = element.getAttribute('data-gsap-duration') ?? 0.6
                    let ease = element.getAttribute('data-gsap-ease') ?? 'power3.out'
                    let distance = element.getAttribute('data-gsap-distance') ?? 50
                    let stagger = element.getAttribute('data-gsap-stagger') ?? 0.1
                    gsap.fromTo(
                        element,
                    {
                        opacity: 0,
                        x: distance,
                        force3D: true
                    },
                    {
                        opacity: 1,
                        x: 0,
                        duration: duration,
                        stagger: stagger as any,
                        delay: delay,
                        ease: ease,
                        force3D: true,
                        willChange: 'transform, opacity',

                        scrollTrigger: {
                            trigger: element,
                            markers: false,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });
                });
            }
            const cardPopups = () => {

                let cards = document.querySelectorAll('[data-gsap="cardPopin"]');

                cards.forEach((card) => {
                    let startVal = card.getAttribute('data-gsap-start') ?? 'top 85%'
                    let endVal = card.getAttribute('data-gsap-end') ?? 'bottom center'
                    let delay = card.getAttribute('data-gsap-delay') ?? 0
                    let initialDistance = card.getAttribute('data-gsap-initial-distance') ?? 50
                    let duration = card.getAttribute('data-gsap-duration') ?? 0.6

                    gsap.fromTo(
                        card,
                    {
                        opacity: 0,
                        y: initialDistance,
                        scale: 0.95,
                        force3D: true
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: duration,
                        delay: delay,
                        ease: "power3.out",
                        force3D: true,
                        willChange: 'transform, opacity',

                        scrollTrigger: {
                            trigger: card,
                            markers: true,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });
                });
            }

            const divPopins = () => {

                // Find all elements with data-gsap="divPopin"
                let elements = document.querySelectorAll('[data-gsap="divPopin"]');
                
                // Group elements by their parent container
                const groupedElements = Array.from(elements).reduce((groups, element) => {
                    const parent = element.parentElement;
                    if (!parent) return groups;
                    
                    const parentId = parent.id || parent.className;
                    if (!groups[parentId]) {
                        groups[parentId] = [];
                    }
                    groups[parentId].push(element);
                    return groups;
                }, {} as Record<string, Element[]>);

                // Animate each group
                Object.values(groupedElements).forEach(group => {
                    if (group.length > 0) {
                        const firstElement = group[0];
                        const parent = firstElement.parentElement;
                        
                        let startVal = firstElement.getAttribute('data-gsap-start') ?? 'top 85%'
                        let endVal = firstElement.getAttribute('data-gsap-end') ?? 'bottom center'
                        let delay = firstElement.getAttribute('data-gsap-delay') ?? 0
                        let initialDistance = firstElement.getAttribute('data-gsap-initial-distance') ?? 37
                        let stagger = firstElement.getAttribute('data-gsap-stagger') ?? 0.15

                        gsap.fromTo(
                            group,
                        {
                            opacity: 0,
                            y: initialDistance,
                            force3D: true
                        },
                        {
                            opacity: 1,
                            y: 0,
                            duration: 0.6,
                            stagger: stagger as any,
                            delay: delay,
                            ease: "power3.out",
                            force3D: true,
                            willChange: 'transform, opacity',

                            scrollTrigger: {
                                trigger: parent,
                                markers: false,
                                start: startVal,
                                end: endVal,
                                scrub: false
                            }
                        });
                    }
                });
            }
            

            const TextAnimations = () => {

                let elements = document.querySelectorAll('[data-gsap="fromStartEnd"]');

                elements.forEach((element) => {

                    let startVal = element.getAttribute('data-gsap-start') ?? 'top 85%'
                    let endVal = element.getAttribute('data-gsap-end') ?? 'bottom center'
                    let delay = element.getAttribute('data-gsap-delay') ?? 0

                    // Add hardware acceleration for better performance
                    gsap.set(element, {
                        transform: 'translateZ(0)',
                        backfaceVisibility: 'hidden'
                    });

                    gsap.fromTo(
                        element,
                    {
                        opacity: 0,
                        y: 40,
                        force3D: true,
                        transform: 'translateZ(0)'
                    },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.5,
                        delay: delay,
                        ease: "easeOut",
                        force3D: true,
                        willChange: 'transform, opacity',

                        scrollTrigger: {
                            trigger: element,
                            markers: false,
                            start: startVal,
                            end: endVal,
                            scrub: false
                        }
                    });
                });
            }

            const imageParallax = (box: Element, scrollTriggerInstance: gsap.plugins.ScrollTriggerInstance) => {

                const viewport = ref<string>('');
                const position = ref<string>('');
                const img = box.querySelector('img');

                const startPosition = scrollTriggerInstance.start;
                const endPosition = scrollTriggerInstance.end;
                const currentPosition = ScrollTrigger.positionInViewport(box, 'top');

                const translateValue = gsap.utils.interpolate(-5, 0, currentPosition);

                const boxElement = box as HTMLElement;
                const boxHeight = boxElement.clientHeight / window.innerHeight * 100; // Convert box height to vh

                boxElement.style.height = `calc(${boxHeight}vh)`;

                if (img) {
                    img.style.height = `calc(${boxHeight}vh + 8vh)`;
                    img.style.transform = `translate3d(0px, ${translateValue}vh, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)`;
                }

                viewport.value = ScrollTrigger.isInViewport(box).toString();
                position.value = currentPosition.toFixed(2);

            };

            const updateAnimations = () => {
                // performanceMonitor.startAnimations();
                
                // Clear any existing ScrollTrigger instances to prevent conflicts
                ScrollTrigger.getAll().forEach(trigger => trigger.kill());
                
                // Skip image parallax ScrollTrigger setup on Safari
                if (!isSafari()) {
                    const images_cards = document.querySelectorAll(".image-parallax");

                    images_cards.forEach((box) => {

                        const scrollTriggerInstance = ScrollTrigger.create({
                            trigger: box,
                            start: "top 160% bottom",
                            end: "bottom top",
                            // markers: true,
                            onUpdate: () => imageParallax(box, scrollTriggerInstance),
                        });

                        imageParallax(box, scrollTriggerInstance);
                    });
                }

                // Full animations for other browsers
                // console.log('✨ Non-Safari browser - using full animations');
                
                HeadingsAnimation();
                ImageAnimations();
                TextAnimations();
                fadeIns();
                divPopins();
                cardPopups();
                fadeInRight();
                
                // performanceMonitor.endAnimations();
                
                // Defer ScrollTrigger refresh to next frame to prevent blocking
                requestAnimationFrame(() => {
                    ScrollTrigger.refresh();
                });
            };

            const isTransitioning = ref(false);
            const isFooterTransitioning = ref(true);
            const isContentLoading = ref(false);

            const onAfterEnter = () => {
                // console.log(`🚀 onAfterEnter called at ${performance.now().toFixed(2)}ms`);
                // performanceMonitor.endVueTransition(); // Track when Vue transition ends
                isTransitioning.value = false;
                
                const scrollStart = performance.now();
                window.scrollTo(0, 0);
                // console.log(`📜 Scroll completed in ${(performance.now() - scrollStart).toFixed(2)}ms`);
                
                // Use requestAnimationFrame for better performance than setTimeout
                requestAnimationFrame(() => {
                    // console.log(`⏰ requestAnimationFrame executed at ${performance.now().toFixed(2)}ms`);
                    
                    if(window.innerWidth >= 768){
                        const animationStart = performance.now();
                        updateAnimations();
                        // console.log(`🎬 updateAnimations completed in ${(performance.now() - animationStart).toFixed(2)}ms`);
                    }
                    
                    // performanceMonitor.endRouteTransition();
                    // performanceMonitor.logReport(); // Log performance after transition
                    // console.log(`✅ Route transition fully completed at ${performance.now().toFixed(2)}ms`);
                }); // Use requestAnimationFrame for optimal timing
            };

            const onBeforeLeave = () => {
                isTransitioning.value = true;
                // performanceMonitor.startRouteTransition();
            };

            const onBeforeEnter = () => {
                isFooterTransitioning.value = true;
            };

            const onFooterAfterLeave = () => {
                isFooterTransitioning.value = false;
            };

            const onFooterBeforeEnter = () => {
                isTransitioning.value = true;
            };

            onMounted(async () => {
                
            });

            // Setup global error handling
            onMounted(() => {
                setupGlobalErrorHandler();
            });

            const handleGlobalError = (errorData: any) => {
                console.error('Global error caught:', errorData);
                // Additional error reporting could be added here
            };

            const handleRetry = () => {
                console.log('Retrying operation...');
                // Force a page refresh as fallback
                window.location.reload();
            };

            return {
                showPopup,
                onClosePopup,
                isTransitioning,
                isContentLoading,
                onAfterEnter,
                onBeforeLeave,
                onFooterBeforeEnter,
                onFooterAfterLeave,
                onBeforeEnter,
                handleGlobalError,
                handleRetry,
                isSafari,
            };
        },
    })

</script>

<style>
.up-enter-from, .up-leave-to{
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    /* Hardware acceleration for smooth transitions */
    transform: translateZ(0);
    will-change: opacity, transform;
    backface-visibility: hidden;
}
.up-enter-active, .up-leave-active{
    transition: opacity 0.2s ease-in-out;
    /* Hardware acceleration for smooth transitions */
    transform: translateZ(0);
    will-change: opacity, transform;
    backface-visibility: hidden;
}

.footer-enter-from, .footer-leave-to{
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    transform: translateZ(0);
    will-change: opacity, transform;
    backface-visibility: hidden;
}
.footer-enter-active, .footer-leave-active{
    transition: opacity 0.3s ease-in-out;
    transform: translateZ(0);
    will-change: opacity, transform;
    backface-visibility: hidden;
}

/* Hardware acceleration for content containers */
.show-content {
    transform: translateZ(0);
    will-change: opacity, transform;
    backface-visibility: hidden;
}

.hide-content {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    transform: translateZ(0);
    will-change: opacity, transform;
    backface-visibility: hidden;
}

.safari-detected{
    opacity: 1 !important;
    transition:0s !important;
}
</style>
